/* import __COLOCATED_TEMPLATE__ from './standing-out.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type { CxBenchmarkGroup, CxBenchmarkReasons } from './types';

type Sentiment = 'positive' | 'negative';

interface StandingOutItem {
  label: string;
  difference: string;
  sentiment: Sentiment;
  dotColorClass: string;
  textColorClass: string;
}

const DOT_COLOR_MAP: Record<Sentiment, string> = {
  positive: 'bg-success-fill',
  negative: 'bg-error-fill',
};

const TEXT_COLOR_MAP: Record<Sentiment, string> = {
  positive: 'text-success',
  negative: 'text-error',
};

const DIFF_THRESHOLD = 0.05;

type ReasonKey = keyof CxBenchmarkReasons;

interface ReasonConfig {
  labelKey: string;
  isDesirable: boolean;
  oppositeKey: ReasonKey;
}

const REASON_CONFIG: Record<ReasonKey, ReasonConfig> = {
  answer_quality_bot_high: {
    labelKey: 'reason-high-fin-answer-quality',
    isDesirable: true,
    oppositeKey: 'answer_quality_bot_low',
  },
  answer_quality_bot_low: {
    labelKey: 'reason-low-fin-answer-quality',
    isDesirable: false,
    oppositeKey: 'answer_quality_bot_high',
  },
  product_feedback_positive: {
    labelKey: 'reason-positive-product-feedback',
    isDesirable: true,
    oppositeKey: 'product_feedback_negative',
  },
  product_feedback_negative: {
    labelKey: 'reason-negative-product-feedback',
    isDesirable: false,
    oppositeKey: 'product_feedback_positive',
  },
  policy_feedback_positive: {
    labelKey: 'reason-positive-policy-feedback',
    isDesirable: true,
    oppositeKey: 'policy_feedback_negative',
  },
  policy_feedback_negative: {
    labelKey: 'reason-negative-policy-feedback',
    isDesirable: false,
    oppositeKey: 'policy_feedback_positive',
  },
  user_strong_emotion_positive: {
    labelKey: 'reason-positive-user-emotion',
    isDesirable: true,
    oppositeKey: 'user_strong_emotion_negative',
  },
  user_strong_emotion_negative: {
    labelKey: 'reason-negative-user-emotion',
    isDesirable: false,
    oppositeKey: 'user_strong_emotion_positive',
  },
  user_effort_low: {
    labelKey: 'reason-low-user-effort',
    isDesirable: true,
    oppositeKey: 'user_effort_high',
  },
  user_effort_high: {
    labelKey: 'reason-high-user-effort',
    isDesirable: false,
    oppositeKey: 'user_effort_low',
  },
};

interface Signature {
  Args: {
    benchmarkGroup: CxBenchmarkGroup | undefined;
    appReasons: CxBenchmarkReasons | undefined;
  };
}

export default class StandingOut extends Component<Signature> {
  @service declare intl: IntlService;

  private t(key: string, options?: Record<string, string>) {
    return this.intl.t(
      `components.reporting.bespoke.fin-performance.cx-benchmark-panel.standing-out.${key}`,
      options,
    );
  }

  get isLoading(): boolean {
    return !this.args.appReasons;
  }

  get items(): StandingOutItem[] {
    let { benchmarkGroup, appReasons } = this.args;
    if (!benchmarkGroup || !appReasons) {
      return [];
    }

    let peerReasons = benchmarkGroup.reasons;

    let results: Array<StandingOutItem & { absDiff: number }> = [];
    let suppressedKeys = new Set<ReasonKey>();

    for (let [key, config] of Object.entries(REASON_CONFIG) as Array<[ReasonKey, ReasonConfig]>) {
      if (suppressedKeys.has(key)) {
        // already eliminated by a stronger opposite — skip
      } else {
        let appRate = appReasons[key];
        let peerRate = peerReasons[key];
        let diff = appRate - peerRate;
        let absDiff = Math.abs(diff);

        let oppositeKey = config.oppositeKey;
        let oppositeAbsDiff = Math.abs(appReasons[oppositeKey] - peerReasons[oppositeKey]);

        let suppressed = false;
        if (absDiff >= DIFF_THRESHOLD && oppositeAbsDiff >= DIFF_THRESHOLD) {
          if (absDiff >= oppositeAbsDiff) {
            suppressedKeys.add(oppositeKey);
          } else {
            suppressedKeys.add(key);
            suppressed = true;
          }
        }

        if (!suppressed && absDiff >= DIFF_THRESHOLD) {
          let isPositive = config.isDesirable ? diff > 0 : diff < 0;
          let sentiment: Sentiment = isPositive ? 'positive' : 'negative';

          let appPct = (appRate * 100).toFixed(1);
          let peerPct = (peerRate * 100).toFixed(1);
          let diffPp = (Math.abs(diff) * 100).toFixed(1);
          let sign = diff > 0 ? '+' : '-';

          let reasonName = this.t(config.labelKey);
          let peerComparison = diff > 0 ? this.t('above-peers') : this.t('below-peers');

          results.push({
            label: this.t('comparison-label', {
              reasonName,
              peerComparison,
              appRate: `${appPct}%`,
              peerRate: `${peerPct}%`,
            }),
            difference: `${sign}${diffPp}pp`,
            sentiment,
            dotColorClass: DOT_COLOR_MAP[sentiment],
            textColorClass: TEXT_COLOR_MAP[sentiment],
            absDiff,
          });
        }
      }
    }

    results.sort((a, b) => b.absDiff - a.absDiff);
    return results;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Bespoke::FinPerformance::CxBenchmarkPanel::StandingOut': typeof StandingOut;
    'reporting/bespoke/fin-performance/cx-benchmark-panel/standing-out': typeof StandingOut;
  }
}
